import { useEffect, useRef } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { notificationKeys } from '@/features/notifications/hooks/useNotifications';
import { taskKeys } from '@/features/tasks/hooks/useTasks';
import { useAuth } from '@/store/AuthContext';
import {
  connectTaskSocket,
  disconnectTaskSocket,
  subscribeTaskChanged,
  subscribeUserAvatar,
  type TaskRealtimeEvent,
  type UserAvatarRealtimeEvent,
} from '@/services/taskRealtime';
import { tokenStorage } from '@/services/tokenStorage';
import type { EmployeeProfile } from '@/types/user';

const ASSIGNEE_AVATAR_QUERY_SUFFIX = 'avatar-v4';
const SOCKET_SYNC_DEBOUNCE_MS = 250;

function syncTaskQueries(queryClient: QueryClient, taskId?: string) {
  void queryClient.invalidateQueries({ queryKey: taskKeys.all });
  void queryClient.refetchQueries({ queryKey: taskKeys.all, type: 'active' });
  void queryClient.invalidateQueries({ queryKey: notificationKeys.unreadCount() });
  void queryClient.invalidateQueries({ queryKey: notificationKeys.list() });
  void queryClient.refetchQueries({
    queryKey: notificationKeys.unreadCount(),
    type: 'active',
  });
  if (taskId) {
    void queryClient.invalidateQueries({ queryKey: taskKeys.detail(taskId) });
    void queryClient.refetchQueries({
      queryKey: taskKeys.detail(taskId),
      type: 'active',
    });
  }
}

function applyAvatarRealtimeUpdate(
  queryClient: QueryClient,
  event: UserAvatarRealtimeEvent,
) {
  const ids = [event.userId, event.employeeRef].filter(
    (id): id is string => Boolean(id),
  );
  for (const id of ids) {
    const key = ['assignees', id, ASSIGNEE_AVATAR_QUERY_SUFFIX] as const;
    queryClient.setQueryData<EmployeeProfile>(key, (prev) =>
      prev ? { ...prev, avatarUrl: event.avatarUrl } : prev,
    );
    void queryClient.invalidateQueries({ queryKey: key, refetchType: 'active' });
  }
}

/**
 * Live task list/notifications via Socket.IO.
 * Always apply events (including self): recurrence cron creates tasks with the
 * rule creator as actorId — skipping self-echo left creators/assignees needing refresh.
 */
export function useTaskRealtimeSync() {
  const { setSession } = useAuth();
  const queryClient = useQueryClient();
  const syncTimer = useRef<number | null>(null);
  const pendingTaskId = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!tokenStorage.getAccessToken()) {
      disconnectTaskSocket();
      return;
    }

    connectTaskSocket();

    const scheduleSync = (taskId?: string) => {
      if (taskId) pendingTaskId.current = taskId;
      if (syncTimer.current) window.clearTimeout(syncTimer.current);
      syncTimer.current = window.setTimeout(() => {
        const id = pendingTaskId.current;
        pendingTaskId.current = undefined;
        syncTimer.current = null;
        syncTaskQueries(queryClient, id);
      }, SOCKET_SYNC_DEBOUNCE_MS);
    };

    const unsubTask = subscribeTaskChanged((event: TaskRealtimeEvent) => {
      // eslint-disable-next-line no-console
      console.info('[task-socket] task:changed', event.type, event.taskId, 'actor=', event.actorId);
      scheduleSync(event.taskId);
    });

    const unsubAvatar = subscribeUserAvatar((event: UserAvatarRealtimeEvent) => {
      if (!event?.userId || !event?.avatarUrl) return;
      applyAvatarRealtimeUpdate(queryClient, event);
      const me = tokenStorage.getUser()?.id;
      if (event.userId === me) {
        const current = tokenStorage.getUser();
        if (current) setSession({ ...current, avatarUrl: event.avatarUrl });
      }
    });

    const reconnectIfNeeded = () => {
      if (document.visibilityState === 'hidden') return;
      if (!tokenStorage.getAccessToken()) return;
      connectTaskSocket();
    };

    document.addEventListener('visibilitychange', reconnectIfNeeded);
    window.addEventListener('online', reconnectIfNeeded);
    window.addEventListener('focus', reconnectIfNeeded);

    return () => {
      if (syncTimer.current) window.clearTimeout(syncTimer.current);
      unsubTask();
      unsubAvatar();
      document.removeEventListener('visibilitychange', reconnectIfNeeded);
      window.removeEventListener('online', reconnectIfNeeded);
      window.removeEventListener('focus', reconnectIfNeeded);
    };
  }, [queryClient, setSession]);
}
